/**
 * Global state management for file uploads using signals, replacing the useFileUpload hook
 *
 * This store replaces the React hook-based file upload functionality with a signal-based approach.
 * It maintains all the features of the original hook while providing better reactivity and global access.
 *
 * Key features:
 * 1. Resumable uploads using Firebase Storage
 * 2. Progress tracking for multiple simultaneous uploads
 * 3. Batch upload support
 * 4. Global upload state accessible from any component
 *
 * Uploads operate on the platform-neutral {@link Asset} type (ELARA-692), not
 * the web `File`, so the orchestration stays identical on React Native — the
 * `StorageContainer` resolves the asset's byte source the way its SDK prefers
 * (a `Blob` on web, the local `uri` handed straight to native `putFile` on RN).
 * Thumbnail generation moved behind the `ImageProcessor` port.
 */

import type { StorageContainer } from '#containers/StorageContainer.js';
import type { Asset } from '#lib/asset.js';

import { createState } from '#lib/signals-utils.js';

/**
 * Represents the state of a single file upload
 */
export interface UploadProgress {
  progress: number;
  state: 'pending' | 'running' | 'paused' | 'success' | 'error';
  error?: Error;
  downloadURL?: string;
}

export function createFileUploadState(storage: StorageContainer) {
  const state = createState({
    /** Map tracking the progress of all uploads */
    uploads: new Map<string, UploadProgress>(),
    /** Get all active uploads (pending or running) */
    get activeUploads(): Map<string, UploadProgress> {
      const active = new Map<string, UploadProgress>();
      for (const [id, upload] of this.uploads) {
        if (upload.state === 'pending' || upload.state === 'running') {
          active.set(id, upload);
        }
      }
      return active;
    },
    /** Check if any uploads are in progress */
    get isUploading(): boolean {
      return this.activeUploads.size > 0;
    },
    /** Get total progress across all active uploads */
    get totalProgress(): number {
      const active = [...this.activeUploads.values()];
      if (active.length === 0) return 100;
      const totalProgress = active.reduce((sum, upload) => sum + upload.progress, 0);
      return totalProgress / active.length;
    },
    /** Updates the upload progress for a specific upload */
    updateUploadProgress(uploadId: string, progress: Partial<UploadProgress>): void {
      const current = this.uploads.get(uploadId);
      if (current) {
        this.uploads = new Map(this.uploads).set(uploadId, { ...current, ...progress });
      } else {
        this.uploads = new Map(this.uploads).set(uploadId, { progress: 0, state: 'pending', ...progress });
      }
    },
    /** Uploads a single asset to Firebase Storage */
    async uploadFile(asset: Asset, path: string, onProgress?: (progress: number) => void): Promise<string> {
      const uploadId = `${asset.name}-${Date.now()}`;
      console.info('[FileUploadState] Starting file upload', { fileName: asset.name, size: asset.size, path });
      try {
        // The container owns the transport (resumable JS-SDK upload on web, native `putFile` on
        // React Native, ELARA-982); this store owns the progress bookkeeping around it.
        const downloadURL = await storage.uploadAsset(path, asset, (progress) => {
          this.updateUploadProgress(uploadId, { progress, state: 'running' });
          onProgress?.(progress);
          console.debug('[FileUploadState] Upload progress', { fileName: asset.name, progress });
        });
        this.updateUploadProgress(uploadId, { progress: 100, state: 'success', downloadURL });
        console.debug('[FileUploadState] Upload completed', { fileName: asset.name, downloadURL });
        return downloadURL;
      } catch (error) {
        console.error('[FileUploadState] Upload failed', { fileName: asset.name, error });
        this.updateUploadProgress(uploadId, { progress: 0, state: 'error', error: error as Error });
        throw error;
      }
    },
    /** Uploads multiple assets to Firebase Storage */
    async uploadFiles(
      assets: Asset[],
      basePath: string,
      onProgress?: (fileName: string, progress: number) => void,
    ): Promise<Array<{ asset: Asset; url: string }>> {
      const results = await Promise.all(
        assets.map(async (asset) => {
          const path = `${basePath}/${asset.name}`;
          const url = await this.uploadFile(asset, path, (progress) => onProgress?.(asset.name, progress));
          return { asset, url };
        }),
      );
      return results;
    },
    /** Clears completed uploads from the uploads map */
    clearCompletedUploads(): void {
      const activeOnly = new Map<string, UploadProgress>();
      for (const [id, upload] of this.uploads) {
        if (upload.state === 'pending' || upload.state === 'running') {
          activeOnly.set(id, upload);
        }
      }
      this.uploads = activeOnly;
    },
    /** Clears all uploads from the uploads map */
    clearAllUploads(): void {
      this.uploads = new Map();
    },
  });
  return state;
}

/** The main reactive store for all file upload functionality. All properties are signals transparent to the user. */
export type FileUploadState = ReturnType<typeof createFileUploadState>;
